import { useCallback, useReducer } from "react";

import { useApi } from "@/api/hooks/useApi";
import type {
  AssistantChatMessageInputRequest,
  AssistantConversationFiltersDto,
  AssistantConversationMessageDto,
  AssistantPageContextRequest,
} from "@/api/types";

import { applyActivityStatusUpdate } from "./activity";
import { readAskNobiStream } from "./readAskNobiStream";
import type {
  AskNobiChatMessagePayload,
  AskNobiPipelineStage,
  AskNobiServerStreamChunk,
  AskNobiToolName,
} from "./types";

interface UseAskNobiChatProps {
  initialMessage?: string;
  defaultFilters?: AssistantConversationFiltersDto;
  onConversationCreated?: (conversationId: string, firstMessage: string) => void;
}

export interface UseAskNobiChatResult {
  messages: AskNobiChatMessagePayload[];
  state: "idle" | "connecting" | "fetching-tools" | "preparing-to-chat" | "chatting";
  pipelineStage: AskNobiPipelineStage | undefined;
  conversationId: string | undefined;
  filters: AssistantConversationFiltersDto;
  sendMessage: (userMessage: string) => Promise<void>;
  clearChat: () => void;
  loadConversation: (
    conversationId: string,
    messages: AssistantConversationMessageDto[],
    filters?: AssistantConversationFiltersDto,
  ) => void;
  changeFilters: (filters: AssistantConversationFiltersDto) => void;
  changeContext: (context: AssistantPageContextRequest | undefined) => void;
}

interface AskNobiFsmState {
  status: "idle" | "connecting" | "streaming";
  messages: AskNobiChatMessagePayload[];
  activeAssistantMessageId: string | undefined;
  activeActivityMessageId: string | undefined;
  activeToolCallIds: Set<string>;
  conversationId: string | undefined;
  filters: AssistantConversationFiltersDto;
  context: AssistantPageContextRequest | undefined;
  pipelineStage: AskNobiPipelineStage | undefined;
}

type AskNobiFsmEvent =
  | { type: "connecting" }
  | { type: "chunk"; payload: AskNobiServerStreamChunk }
  | { type: "sendMessage"; payload: string }
  | { type: "reset" }
  | {
      type: "loadConversation";
      conversationId: string;
      messages: AssistantConversationMessageDto[];
      filters?: AssistantConversationFiltersDto;
    }
  | { type: "changeFilters"; filters: AssistantConversationFiltersDto }
  | { type: "changeContext"; context: AssistantPageContextRequest | undefined }
  | { type: "error"; content: string };

export function useAskNobiChat({
  initialMessage,
  defaultFilters,
  onConversationCreated,
}: UseAskNobiChatProps): UseAskNobiChatResult {
  const api = useApi();

  const [state, dispatch] = useReducer(aiStateReducer, {
    status: "idle",
    messages: initialMessage
      ? [
          {
            id: crypto.randomUUID(),
            sender: "assistant",
            type: "text",
            content: initialMessage,
          },
        ]
      : [],
    activeAssistantMessageId: undefined,
    activeActivityMessageId: undefined,
    activeToolCallIds: new Set<string>(),
    conversationId: undefined,
    filters: defaultFilters || {},
    context: undefined,
    pipelineStage: undefined,
  });

  const isProcessing = state.status !== "idle" || state.activeToolCallIds.size > 0;

  const sendMessage = useCallback(
    async (userMessage: string) => {
      const cleanedMessage = userMessage.trim();
      if (!cleanedMessage || isProcessing) {
        return;
      }

      dispatch({
        type: "sendMessage",
        payload: cleanedMessage,
      });

      const messages: AssistantChatMessageInputRequest[] = [{ role: "user", content: cleanedMessage }];
      const isNewConversation = !state.conversationId;

      try {
        dispatch({ type: "connecting" });
        await readAskNobiStream({
          request: () =>
            api.postAssistantChatV1(
              {
                conversationId: state.conversationId,
                messages,
                ...state.filters,
                projectIds: state.filters?.projectIds ?? [],
                thinkingLevel: "medium",
                pageContext: state.context,
              },
              { format: undefined },
            ),
          callbacks: {
            onChunk: (chunk) => {
              dispatch({ type: "chunk", payload: chunk });
              if (chunk.chunkType === "conversation" && isNewConversation) {
                onConversationCreated?.(chunk.conversationId, cleanedMessage);
              }
            },
            onError: (err) =>
              dispatch({
                type: "error",
                content: err.message,
              }),
          },
        });
      } catch (err) {
        dispatch({
          type: "error",
          content: err instanceof Error ? err.message : "Unknown error",
        });
      }
    },
    [api, isProcessing, onConversationCreated, state.context, state.filters, state.conversationId],
  );

  const clearChat = useCallback(() => dispatch({ type: "reset" }), []);

  const loadConversation = useCallback(
    (conversationId: string, messages: AssistantConversationMessageDto[], filters?: AssistantConversationFiltersDto) =>
      dispatch({ type: "loadConversation", conversationId, messages, filters }),
    [],
  );

  const changeFilters = useCallback(
    (filters: AssistantConversationFiltersDto) => dispatch({ type: "changeFilters", filters }),
    [],
  );

  const changeContext = useCallback(
    (context: AssistantPageContextRequest | undefined) => dispatch({ type: "changeContext", context }),
    [],
  );

  const calculateState = () => {
    if (state.status === "connecting") return "connecting";
    if (state.activeToolCallIds.size > 0) return "fetching-tools";
    if (state.status !== "streaming") return "idle";

    // We determine if the assistant has started typing a message yet
    const lastMessage = state.messages.at(-1);
    const isPreparingToChat =
      !lastMessage ||
      lastMessage.sender === "user" ||
      (lastMessage.sender === "assistant" && lastMessage.type !== "text") ||
      (lastMessage.sender === "assistant" && lastMessage.type === "text" && lastMessage.content.length === 0);

    return isPreparingToChat ? "preparing-to-chat" : "chatting";
  };

  return {
    messages: state.messages,
    state: calculateState(),
    pipelineStage: state.pipelineStage,
    conversationId: state.conversationId,
    filters: state.filters,
    sendMessage,
    clearChat,
    loadConversation,
    changeFilters,
    changeContext,
  };
}

function aiStateReducer(state: AskNobiFsmState, event: AskNobiFsmEvent): AskNobiFsmState {
  switch (event.type) {
    case "connecting":
      return {
        ...state,
        status: "connecting",
      };

    case "chunk":
      return aiStateChunkReducer(state, event.payload);

    case "sendMessage": {
      return {
        ...state,
        pipelineStage: undefined,
        messages: [
          ...state.messages,
          {
            id: crypto.randomUUID(),
            sender: "user",
            type: "text",
            content: event.payload,
          },
        ],
      };
    }

    case "reset":
      return {
        status: "idle",
        messages: [],
        activeAssistantMessageId: undefined,
        activeActivityMessageId: undefined,
        activeToolCallIds: new Set(),
        conversationId: undefined,
        filters: {},
        context: undefined,
        pipelineStage: undefined,
      };

    case "loadConversation":
      return {
        status: "idle",
        messages: event.messages.flatMap((m): AskNobiChatMessagePayload[] => {
          if (m.role === "tool") {
            try {
              const parsed = JSON.parse(m.content) as { tool_name: AskNobiToolName; call_id: string };

              return [
                {
                  id: m.id,
                  sender: "assistant",
                  type: "tool_execution",
                  toolName: parsed.tool_name,
                  callId: parsed.call_id,
                  status: "done",
                  historical: true,
                },
              ];
            } catch {
              return [];
            }
          }

          return [
            {
              id: m.id,
              sender: m.role,
              type: "text",
              content: m.content,
              followUpSuggestions: m.followUpSuggestions,
              historical: true,
              myRating: m.myRating,
            },
          ];
        }),
        activeAssistantMessageId: undefined,
        activeActivityMessageId: undefined,
        activeToolCallIds: new Set(),
        conversationId: event.conversationId,
        filters: event.filters || {},
        context: state.context,
        pipelineStage: undefined,
      };

    case "changeFilters":
      return {
        ...state,
        filters: event.filters,
      };

    case "changeContext":
      return {
        ...state,
        context: event.context,
      };

    case "error":
      return aiStateChunkReducer(state, {
        chunkType: "error",
        content: event.content,
      });
  }
}

function aiStateChunkReducer(state: AskNobiFsmState, chunk: AskNobiServerStreamChunk): AskNobiFsmState {
  switch (chunk.chunkType) {
    case "streamingStart": {
      const activityMessageId = crypto.randomUUID();

      return {
        ...state,
        status: "streaming",
        pipelineStage: undefined,
        activeActivityMessageId: activityMessageId,
        messages: [
          ...state.messages,
          {
            id: activityMessageId,
            sender: "assistant",
            type: "activity",
            status: "running",
            startedAt: Date.now(),
            items: [],
          },
        ],
      };
    }

    case "statusUpdate":
      return {
        ...state,
        pipelineStage: chunk.stage,
        messages: state.messages.map((message) => {
          if (message.id !== state.activeActivityMessageId || message.type !== "activity") {
            return message;
          }

          return { ...message, items: applyActivityStatusUpdate(message.items, chunk) };
        }),
      };

    case "messageStart": {
      if (chunk.role !== "assistant") return state;

      return {
        ...state,
        status: "streaming",
        activeAssistantMessageId: chunk.messageId,
        messages: [
          ...state.messages,
          {
            id: chunk.messageId,
            sender: "assistant",
            type: "text",
            content: "",
            streaming: true,
          },
        ],
      };
    }

    case "textContent": {
      if (!state.activeAssistantMessageId) return state;

      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === state.activeAssistantMessageId && m.type === "text"
            ? { ...m, content: m.content + chunk.content }
            : m,
        ),
      };
    }

    case "followUpSuggestions":
      return {
        ...state,
        messages: state.messages.map((message) =>
          message.id === chunk.messageId && message.type === "text"
            ? { ...message, followUpSuggestions: chunk.suggestions }
            : message,
        ),
      };

    case "messageEnd":
      if (chunk.role !== "assistant") return state;

      return {
        ...state,
        status: "idle",
        activeAssistantMessageId: undefined,
        activeActivityMessageId: undefined,
        pipelineStage: undefined,
        messages: state.messages.map((message) => {
          if (message.id === state.activeAssistantMessageId && message.type === "text") {
            return { ...message, streaming: false };
          }

          if (message.id === state.activeActivityMessageId && message.type === "activity") {
            return {
              ...message,
              status: "done",
              completedAt: Date.now(),
              items: message.items.map((item) =>
                item.status === "running" ? { ...item, status: "done" as const } : item,
              ),
            };
          }

          return message;
        }),
      };

    case "toolCall": {
      const next = new Set(state.activeToolCallIds);
      next.add(chunk.callId);

      return {
        ...state,
        activeToolCallIds: next,
        status: "streaming",
        messages: state.messages.map((message) =>
          message.id === state.activeActivityMessageId && message.type === "activity"
            ? {
                ...message,
                items: [
                  ...message.items.map((item) =>
                    item.type === "stage" && item.status === "running" ? { ...item, status: "done" as const } : item,
                  ),
                  {
                    type: "tool" as const,
                    toolName: chunk.toolName,
                    callId: chunk.callId,
                    status: "running" as const,
                  },
                ],
              }
            : message,
        ),
      };
    }

    case "toolResult": {
      const next = new Set(state.activeToolCallIds);
      next.delete(chunk.callId);

      const hasErrors =
        Array.isArray(chunk.result) && chunk.result.some((r: any) => Array.isArray(r.errors) && r.errors.length > 0);

      return {
        ...state,
        activeToolCallIds: next,
        messages: state.messages.map((message) => {
          if (message.type === "activity" && message.id === state.activeActivityMessageId) {
            return {
              ...message,
              items: message.items.map((item) =>
                item.type === "tool" && item.callId === chunk.callId
                  ? { ...item, status: hasErrors ? ("error" as const) : ("done" as const) }
                  : item,
              ),
            };
          }

          if (message.type === "tool_execution" && message.callId === chunk.callId) {
            return {
              ...message,
              status: hasErrors ? "error" : "done",
              result: chunk.result,
            };
          }

          return message;
        }),
      };
    }

    case "conversation":
      return {
        ...state,
        conversationId: chunk.conversationId,
      };

    case "error":
      return {
        ...state,
        status: "idle",
        activeAssistantMessageId: undefined,
        activeActivityMessageId: undefined,
        activeToolCallIds: new Set(),
        pipelineStage: undefined,
        messages: [
          ...state.messages.map((message) =>
            message.id === state.activeActivityMessageId && message.type === "activity"
              ? {
                  ...message,
                  status: "error" as const,
                  completedAt: Date.now(),
                  items: message.items.map((item) =>
                    item.status === "running" ? { ...item, status: "error" as const } : item,
                  ),
                }
              : message,
          ),
          {
            id: crypto.randomUUID(),
            sender: "assistant",
            type: "error",
            content: chunk.content,
          },
        ],
      };

    default:
      return state;
  }
}
